import { env } from '@/config/env'

/**
 * Every path the app sends to VFS's backend, in one place.
 *
 * The routes used to be literals at their call sites, which was fine while every
 * deployment served them at the same `/api/v1`. UAT does not: bolt21apiuat.vfsglobal.com
 * namespaces them per tenant, so its token endpoint is `/api/v1/DHASID/auth/token`.
 * That segment is configuration (`VITE_VFS_API_PREFIX`), not a constant, and gathering
 * the routes here is what keeps a namespaced deployment from being seven edits.
 *
 * The prefix belongs here rather than in the base URL because `buildVfsClientUrl` is plain
 * concatenation: a base carrying the namespace would send it twice. `src/config/env.ts`
 * refuses such a base outright.
 *
 * These are paths ONLY. The `?idNo=` query parameter that most `application.*` routes
 * require is passed through `vfsClientRequest`'s `query` option instead of being baked in
 * here — otherwise this flat table would become a dozen functions differing only in an
 * argument the transport can escape once and correctly.
 */
const prefix = env.vfsClientApiPrefix

export const vfsClientRoutes = {
  /**
   * §4.1a — the anonymous session cookie, and the one route that needs no session.
   * There is no `auth/token` here any more: that endpoint is for confidential
   * clients with a real secret, which a browser is not (dev plan §3.2).
   */
  session: `${prefix}/session`,
  /**
   * Ends the session above. A `POST` to a sub-path rather than `DELETE /session`,
   * which is what the backend now exposes; the browser semantics are unchanged —
   * anonymous, idempotent, and it only ever clears the cookie.
   */
  sessionDelete: `${prefix}/session/delete`,
  /** Step 6 — the officer's sign-in row. */
  vacAuthDetails: `${prefix}/vac-auth-details`,
  /** Step 7 — how the in-person check resolved, against the row above. */
  vacAuthVerification: (vacAuthId: number): string =>
    `${prefix}/vac-auth-details/${String(vacAuthId)}/verification`,

  /**
   * §4.8 — DHA's application lifecycle, which VFS's backend now calls on our behalf.
   *
   * These replace the direct-to-DHA gateway calls the app used to make against
   * DHA's own gateway. The backend holds the DHA token, so the browser never sees
   * one, and every call here is logged server-side — which is why the client-side
   * audit sink was removed rather than repointed.
   */
  application: {
    eligibility: `${prefix}/application/eligibility`,
    initiate: `${prefix}/application/initiate`,
    verifyFacialBiometrics: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/verify-facial-biometrics`,
    createApplication: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/create-application`,
    contactDetails: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/contact-details`,
    addressDetails: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/address-details`,
    declaration: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/declaration`,
    deliveryDetails: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/delivery-details`,
    finalise: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/finalise`,
    status: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/status`,
    outstandingRequirements: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/outstanding-requirements`,
    cancel: (appRefNo: string): string =>
      `${prefix}/application/${encodeURIComponent(appRefNo)}/cancel`,
    /** The one lifecycle route taking the ID number in the path rather than the query. */
    openApplications: (idNo: string): string =>
      `${prefix}/application/open-applications/${encodeURIComponent(idNo)}`,
    /** No parameters, and cacheable indefinitely — these rarely change. */
    static: {
      branches: `${prefix}/application/static/branches`,
      declarationWording: `${prefix}/application/static/declaration-wording`,
      documentCosts: `${prefix}/application/static/document-costs`,
      couriers: `${prefix}/application/static/couriers`,
    },
  },
} as const
